import axios from 'axios';
import {
  CandidateAiDataBuilder,
  CandidateAIScoreOutput,
  GetCandidateAiDataOutput,
  GetCandidateScoreSlugsOutput,
  CandidateAIScoreType,
  GetCandidateAiScoreOutput,
} from '../candidate-ai.types';

export const getCandidateAiScores = (builder: CandidateAiDataBuilder) =>
  builder.query<GetCandidateAiScoreOutput, number>({
    queryFn: async (candidateId) => {
      const axiosOptions = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('id_token')}`,
        },
      };
      /**
       * Gets all the scores types
       */
      const candidateScoresTypes: GetCandidateScoreSlugsOutput =
        (
          await axios.get(
            `${import.meta.env.REACT_APP_API_URL}/candidate-scores/candidate-score-types`,
            axiosOptions
          )
        )?.data || [];

      /* 
      Priority:
      1. API: candidate-scores/{candidateId}
      2. API: /candidates/ai/{candidateId} // should be deprecated in the near future
      */
      const candidateScores = (
        (
          await axios.get<CandidateAIScoreOutput>(
            `${import.meta.env.REACT_APP_API_URL}/candidate-scores/${candidateId}`,
            axiosOptions
          )
        )?.data || []
      )
        /* Removes CandidateScoreType prop name to match the structure and converts the values */
        .map(({ CandidateScoreType, FinalScore, FinalValue, ...rest }) => ({
          ...rest,
          ...CandidateScoreType,
          FinalValue:
            CandidateScoreType.ScoreType === CandidateAIScoreType.numeric
              ? Math.round(FinalValue * 100)
              : FinalValue,
          FinalScore: Math.round(FinalScore * 100),
        }));

      const candidateAiOldScores: GetCandidateAiDataOutput =
        (
          await axios.get(
            `${import.meta.env.REACT_APP_API_URL}/candidates/ai/${candidateId}`,
            axiosOptions
          )
        )?.data || {};

      const formattedData = candidateScoresTypes.map((singleScoreType: any) => {
        const matchedScoreBySlug = candidateScores.find(
          (singleScore) => singleScore.Slug === singleScoreType.Slug
        );

        if (matchedScoreBySlug) return matchedScoreBySlug;
        /**
         * Old AI Score mappings, should be get deleted in the near future
         */
        switch (singleScoreType.Slug.toLowerCase()) {
          case 'communication':
          case 'leadership':
          case 'adaptability':
          case 'critical-thinking':
          case 'teamwork':
          case 'problem-solving':
          case 'organization':
          case 'creativity':
          case 'persuasion':
          case 'open-to-criticism':
          case 'time-management':
          case 'empathy':
          case 'conflict-resolution': {
            const propName = singleScoreType.Slug.toLowerCase().replace(
              /-/g,
              '_'
            ) as keyof typeof candidateAiOldScores.soft_skills;
            singleScoreType.FinalValue = Math.round(
              (candidateAiOldScores?.soft_skills?.[propName] ?? 0) * 100
            );
            singleScoreType.FinalScore = singleScoreType.FinalValue;
            break;
          }
          case 'simera-english':
            singleScoreType.FinalValue = Math.round(
              (candidateAiOldScores?.english_language_proficiency
                ?.simera_score ?? 0) * 100
            );
            singleScoreType.FinalScore = singleScoreType.FinalValue;
            singleScoreType.OldAiData = true;
            break;
          case 'loyalty':
            singleScoreType.FinalValue = Math.round(
              (candidateAiOldScores?.jump_around?.score ?? 0) * 100
            );
            singleScoreType.FinalScore = singleScoreType.FinalValue;
            break;
          case 'role-fit':
            singleScoreType.FinalValue = Math.round(
              (candidateAiOldScores?.job_fits?.reduce((prev, act) => {
                return act.match_score > prev ? act.match_score : prev;
              }, 0) ?? 0) * 100
            );
            singleScoreType.FinalScore = singleScoreType.FinalValue;
            singleScoreType.OldAiData = true;
            break;

          /**
           * New score types, doesnt exists on old ai scores so mappings aren't needed
           */
          default:
            singleScoreType.FinalScore = 0;

            if (singleScoreType.ScoreType === CandidateAIScoreType.numeric) {
              singleScoreType.FinalValue = 0;
            }

            /* Selects the lower value */
            if (singleScoreType.ScoreType === CandidateAIScoreType.select)
              singleScoreType.FinalValue = Object.keys(
                singleScoreType.Config.numericMapping
              ).reduce((prev, act) => {
                if (!prev) return act;
                return singleScoreType.Config.numericMapping[prev] >
                  singleScoreType.Config.numericMapping[act]
                  ? act
                  : prev;
              }, '');
            break;
        }

        return singleScoreType;
      });

      return { data: formattedData };
    },
    providesTags: (results, args, meta) => [{ type: 'aiScore', id: meta }],
  });
